import React, { useState } from "react";
import PropTypes from "prop-types";
import styled, { useTheme } from "styled-components";
import { useRouter } from "next/router";
import SearchSortDropDown from "fields/SearchSortDropDown";
import Icon from "components/icons";
import useWindowSize from "hooks/use-window-size";
import PopoverComponent from "components/Popover";
import LegacyIcon from "components/icons/legacy-icon";

const ResultsHeaderContainer = styled.div`
  background-color: ${({ theme }) => theme.themeColors.white};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  position: sticky;
  top: 0;
  z-index: 2;

  ${({ theme }) => theme.mq.xl`
    display: grid;
    position: relative;
    width: 56.5vw;
  `}
`;

const ProviderFilterWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  grid-row: 1;
  margin: 0;
`;

const ProviderCount = styled.h3`
  font-size: ${({ theme }) => theme.fontSize(4)};
  letter-spacing: -0.025rem;
  margin: 0;
`;

const SortDropDownWrapper = styled.div`
  display: ${({ activeView }) => (activeView === "map" && "none") || "flex"};
  align-items: center;
  justify-content: space-between;
  grid-row: 1;
  margin: 0.75rem 0;

  ${({ theme }) => theme.mq.xl`
    display: flex;
    margin: 0;
  `}
`;

const SortDropDownOptions = [
  {
    label: "Top Rated",
    value: "top-rated",
  },
  {
    label: "Distance (nearest)",
    value: "distance",
  },
];

const ListMapFilter = styled.div`
  color: ${({ theme }) => theme.themeColors.black};
  display: flex;
  font-size: 0.875rem;

  ${({ theme }) => theme.mq.xl`
    display: none;
  `}
`;

const FilterButton = styled.button`
  background-color: transparent;
  margin: 0 0.5rem;
  border: none;
  cursor: pointer;
  padding: 0;

  :focus {
    outline: 0;
  }

  :focus-visible {
    outline: 0.125rem solid #0070f3;
  }
`;

const ListButton = styled(FilterButton)`
  color: ${({ activeView, theme }) => (activeView === "list" && theme.themeColors.darkGreen1) || theme.themeColors.darkGreen3};

  rect {
    stroke: ${({ activeView, theme }) => (activeView === "list" && theme.themeColors.darkGreen1) || theme.themeColors.darkGreen3};
  }
`;

const MapButton = styled(FilterButton)`
  color: ${({ activeView, theme }) => (activeView === "map" && theme.themeColors.darkGreen1) || theme.themeColors.darkGreen3};
  margin: 0 0 0 0.5rem;

  path {
    stroke: ${({ activeView, theme }) => (activeView === "map" && theme.themeColors.darkGreen1) || theme.themeColors.darkGreen3};
  }
`;

const FilterText = styled.span`
  margin: 0 0.25rem;
`;

const PopOverWrapper = styled.div`
  display: ${({ activeView }) => (activeView === "list" && "flex") || "none"};
  margin: 0 0 0.75rem;
  position: relative;
  max-width: 23rem;

  ${({ theme }) => theme.mq.xl`
    display: flex;
    margin: 0.75rem 0 0.75rem;
  `}
`;

const PopOverReference = styled.div`
  border-radius: 1rem;
  box-shadow: 0 0.125rem 0.375rem ${({ theme }) => theme.themeColors.darkGreen1Point1Opacity};
  line-height: 1;
  padding: 1rem 0.75rem 0.75rem;
`;

const PopOverTitle = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0 0 0.75rem;
`;

const PopOverText = styled.div`
  color: ${({ theme }) => theme.themeColors.darkGreen4};
  font-size: 0.875rem;
  font-weight: 500;
  margin: 0 0 1rem;

  :nth-of-type(5) {
    margin-bottom: 0;
  }

  span {
    font-weight: 600;
  }
`;

const setDropDownValues = (val) => {
  return (val.value === "top-rated" && SortDropDownOptions[0]) || SortDropDownOptions[1];
};

function BusinessResultsHeader({ recordCount, selectedSortOption, activeView, setActiveView }) {
  const theme = useTheme();
  const { query, push } = useRouter();
  const [selectedSort, setSelectedSort] = useState({
    label: selectedSortOption || "Top Rated",
    value: selectedSortOption || "top-rated",
  });
  const [popOverElement, setPopOverElement] = useState(null);
  const handleChange = (val) => {
    push({
      query: {
        ...query,
        sort: val?.value,
      },
    });
    setSelectedSort({
      label: val?.label,
      value: val?.value,
    });
  };
  const handleViewChange = (view) => {
    setActiveView(view);
  };
  return (
    <ResultsHeaderContainer>
      <ProviderFilterWrapper>
        <ProviderCount>{recordCount} Providers</ProviderCount>
        <ListMapFilter>
          <ListButton activeView={activeView} onClick={() => handleViewChange("list")}>
            <Icon name="listView" />
            <FilterText>List</FilterText>
          </ListButton>
          |
          <MapButton activeView={activeView} onClick={() => handleViewChange("map")}>
            <Icon name="mapView" />
            <FilterText>Map</FilterText>
          </MapButton>
        </ListMapFilter>
      </ProviderFilterWrapper>
      <SortDropDownWrapper activeView={activeView}>
        <SearchSortDropDown
          options={SortDropDownOptions}
          onChange={handleChange}
          value={[
            setDropDownValues(
              selectedSort || {
                label: "Top Rated",
                value: "top-rated",
              }
            ),
          ]}
        />
      </SortDropDownWrapper>
      <PopOverWrapper activeView={activeView}>
        <PopOverReference ref={setPopOverElement}>
          How booking works on GoodFriend <LegacyIcon name="info-16" color={theme.themeColors.goodfriendDark} />
        </PopOverReference>
        <PopoverComponent showClose referenceElement={popOverElement} placement={(useWindowSize().width < 768 && "bottom") || "right"}>
          <>
            <PopOverTitle>How Booking on GoodFriend Works</PopOverTitle>
            <PopOverText>
              <span>Request appointment:</span> The GoodFriend team handles all your scheduling details. Find the right provider for your
              pet and request an appointment. We’ll work with the provider to book the appointment for you within 24 hours.
            </PopOverText>
            <PopOverText>
              <span>Book now:</span> Select an available date and time that works for you. We'll ask you to submit some basic information
              about your pet and chosen service(s) and the provider will confirm your appointment.
            </PopOverText>
            <PopOverText>
              <span>Call to book:</span> Contact a provider directly to book an appointment.
            </PopOverText>
          </>
        </PopoverComponent>
      </PopOverWrapper>
    </ResultsHeaderContainer>
  );
}

export default BusinessResultsHeader;

BusinessResultsHeader.propTypes = {
  activeView: PropTypes.string,
  setActiveView: PropTypes.func,
  recordCount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedSortOption: PropTypes.string,
  theme: PropTypes.shape({
    themeColors: PropTypes.shape({
      secondary: PropTypes.string,
    }),
  }),
};

BusinessResultsHeader.defaultProps = {
  activeView: "list",
  setActiveView: () => {},
  recordCount: undefined,
  selectedSortOption: "",
  theme: {
    secondary: undefined,
  },
};
